import { useCallback, useEffect, useRef } from 'react';

const DEFAULT_POLL_INTERVAL_MS = Number(
  process.env.REACT_APP_SUBSCRIPTION_QUOTA_POLL_INTERVAL_MS || 45000
);

const MIN_FOCUS_REFRESH_MS = Number(
  process.env.REACT_APP_SUBSCRIPTION_FOCUS_REFRESH_MS || 30000
);

/**
 * Periodically reloads subscription plan + quota state so booster plan / package
 * upgrades from the Self-Onboarding Portal apply without a page refresh.
 * Intended to be mounted once (MainLayout) — not in panels or page hooks.
 */
export default function useSubscriptionQuotaRefresh({
  enabled = true,
  onRefresh,
}) {
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;
  const lastRefreshAtRef = useRef(0);

  const refresh = useCallback(async (options = {}) => {
    if (!enabled) {
      return;
    }

    const { force = false } = options;
    const now = Date.now();

    if (!force && now - lastRefreshAtRef.current < MIN_FOCUS_REFRESH_MS) {
      return;
    }

    lastRefreshAtRef.current = now;
    await onRefreshRef.current?.();
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    refresh({ force: true });

    const intervalId = setInterval(
      () => refresh({ force: true }),
      DEFAULT_POLL_INTERVAL_MS
    );

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        refresh();
      }
    };

    const handleFocus = () => refresh();

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [enabled, refresh]);

  return { refreshQuotaStatus: () => refresh({ force: true }) };
}
